import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
} from '@mui/material';
import {
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Sector } from 'recharts';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

function BooksPerLibrary({ stats, isLoading }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeIndex, setActiveIndex] = React.useState(null);
  const [animatedRadius, setAnimatedRadius] = React.useState(100);
  const animationRef = React.useRef(null);
  const animatedRadiusRef = React.useRef(100);
  const totalLivres = stats?.totalLivres || 0;

  // Debug: vérifier les données reçues
  console.log('BooksPerLibrary - stats:', stats);
  console.log('BooksPerLibrary - livresParBibliotheque:', stats?.livresParBibliotheque);

  // Préparer les données pour le graphique en camembert
  const pieData = stats?.livresParBibliotheque && Array.isArray(stats.livresParBibliotheque) && stats.livresParBibliotheque.length > 0
    ? stats.livresParBibliotheque
        .map((biblio) => {
          const nombreLivres = parseInt(biblio.nombre_livres) || 0;
          return {
            name: biblio.nom || t('homePage.unnamed'),
            value: nombreLivres,
            percentage: totalLivres > 0 ? Math.round((nombreLivres / totalLivres) * 100) : 0,
            bibliothequeId: biblio.id, // Ajouter l'ID pour la navigation
          };
        })
        .filter(item => item.value > 0)
    : [];

  console.log('BooksPerLibrary - pieData:', pieData);

  // Générer des couleurs dynamiquement pour chaque bibliothèque
  // Utilisation d'une palette de couleurs variées
  const colorPalette = [
    '#2196f3', // Bleu
    '#9c27b0', // Violet
    '#e91e63', // Rose
    '#00bcd4', // Cyan
    '#4caf50', // Vert
    '#ff9800', // Orange
    '#795548', // Marron
    '#607d8b', // Bleu-gris
  ];

  // Format personnalisé pour les labels : "Nom (nombre)"
  const renderCustomLabel = (entry) => {
    return `${entry.name} (${entry.value})`;
  };

  // Fonction pour rendre le secteur actif avec un effet de zoom
  const renderActiveShape = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
    // Utiliser le rayon animé au lieu d'un zoom fixe
    const zoomAmount = animatedRadius - 100;
    return (
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + zoomAmount}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        style={{ cursor: 'pointer' }}
      />
    );
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  const onPieClick = (_, index) => {
    // Récupérer l'ID de la bibliothèque correspondant à l'index cliqué
    if (index >= 0 && index < pieData.length) {
      const clickedBibliothequeId = pieData[index].bibliothequeId;
      if (clickedBibliothequeId) {
        // Naviguer vers la fiche de la bibliothèque
        navigate(`/bibliotheques/${clickedBibliothequeId}`);
      }
    }
  };

  // Animation du rayon avec requestAnimationFrame
  React.useEffect(() => {
    // Annuler toute animation en cours
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    // Toujours démarrer depuis la valeur actuelle stockée dans la ref
    const startRadius = animatedRadiusRef.current;
    const targetRadius = activeIndex !== null ? 105 : 100; // 5px de zoom
    
    // Si on passe d'un secteur à un autre (activeIndex change mais reste non-null),
    // réinitialiser le rayon à 100 pour que l'animation soit visible
    if (activeIndex !== null && startRadius > 100) {
      animatedRadiusRef.current = 100;
      setAnimatedRadius(100);
    }
    
    const duration = 300; // 300ms
    const startTime = Date.now();
    const actualStartRadius = activeIndex !== null && startRadius > 100 ? 100 : startRadius;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function (ease-out cubic)
      const eased = 1 - Math.pow(1 - progress, 3);
      
      const currentRadius = actualStartRadius + (targetRadius - actualStartRadius) * eased;
      setAnimatedRadius(currentRadius);
      animatedRadiusRef.current = currentRadius; // Mettre à jour la ref

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        // S'assurer que la valeur finale est exactement la cible
        setAnimatedRadius(targetRadius);
        animatedRadiusRef.current = targetRadius;
        animationRef.current = null;
      }
    };

    // Démarrer l'animation immédiatement
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex]); // Ne pas inclure animatedRadius pour éviter les boucles

  if (isLoading) {
    return null;
  }

  if (!stats?.livresParBibliotheque || !Array.isArray(stats.livresParBibliotheque) || pieData.length === 0) {
    console.log('BooksPerLibrary - Pas de données à afficher', {
      hasStats: !!stats,
      hasLivresParBibliotheque: !!stats?.livresParBibliotheque,
      isArray: Array.isArray(stats?.livresParBibliotheque),
      length: stats?.livresParBibliotheque?.length,
      pieDataLength: pieData.length
    });
    return null;
  }

  return (
    <Card sx={{ borderRadius: 2, boxShadow: 2, mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <BarChartIcon sx={{ fontSize: 24, color: 'primary.main', mr: 1 }} />
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600 }}>
            {t('homePage.booksPerLibrary')}
          </Typography>
        </Box>
        <Box
          sx={{
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            '& svg': {
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            },
            '& *': {
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
            },
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            return false;
          }}
          onSelectStart={(e) => {
            e.preventDefault();
            return false;
          }}
        >
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomLabel}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                onMouseEnter={onPieEnter}
                onMouseLeave={onPieLeave}
                onClick={onPieClick}
                isAnimationActive={true}
                animationDuration={400}
                animationEasing="ease-out"
              >
                {pieData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={colorPalette[index % colorPalette.length]}
                    style={{ 
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
}

export default BooksPerLibrary;

